// src/components/intentions/TILActivitiesSection.tsx
import React from 'react';
import { CheckSquare, Info, AlertCircle } from 'lucide-react';
import { TIL_ACTIVITIES } from '../../types/intentionPeriods';

interface TILActivitiesSectionProps {
  selectedActivities: string[];
  onChange: (activities: string[]) => void;
  disabled?: boolean;
}

const MINIMUM_SELECTIONS = 5;

const TILActivitiesSection: React.FC<TILActivitiesSectionProps> = ({
  selectedActivities,
  onChange,
  disabled = false
}) => {
  const handleActivityToggle = (activity: string) => {
    const isSelected = selectedActivities.includes(activity);
    if (isSelected) {
      onChange(selectedActivities.filter(a => a !== activity));
    } else {
      onChange([...selectedActivities, activity]);
    }
  };

  const selectionCount = selectedActivities.length;
  const hasMinimum = selectionCount >= MINIMUM_SELECTIONS;
  const remaining = MINIMUM_SELECTIONS - selectionCount;

  return (
    <div className="bg-white rounded-lg border border-ink-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-ink-900 flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-ink-500" />
            TIL Activity Preferences
          </h3>
          <p className="text-sm text-ink-500 mt-1">
            Select activities you are willing to participate in during the school year
          </p>
        </div>

        {/* Selection Counter */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
          hasMinimum
            ? 'bg-ok-bg text-ok-fg'
            : 'bg-warn-bg text-warn-fg'
        }`}>
          <span className="text-sm font-medium font-mono tabular-nums">
            {selectionCount} / {MINIMUM_SELECTIONS} min
          </span>
          {hasMinimum && <CheckSquare className="w-4 h-4" />}
        </div>
      </div>

      {/* Information Box */}
      <div className="mb-6 p-4 bg-navy-50 rounded-lg border border-navy-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-navy-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-navy-800">
            <p className="font-medium mb-1">About TIL (Time in Lieu) Activities</p>
            <p>
              As per CEMEA (2022), teaching staff are required to participate in 70 hours of
              TIL activities across the school year. Please select at least {MINIMUM_SELECTIONS} activities
              you are willing to be involved in. Your preferences will be considered when
              allocating duties.
            </p>
          </div>
        </div>
      </div>

      {/* Validation Warning */}
      {!hasMinimum && (
        <div className="mb-4 p-3 bg-warn-bg rounded-lg border border-warn-border flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-warn-fg flex-shrink-0" />
          <span className="text-sm text-warn-fg">
            Please select {remaining} more {remaining === 1 ? 'activity' : 'activities'} (minimum {MINIMUM_SELECTIONS} required)
          </span>
        </div>
      )}

      {/* Activities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {TIL_ACTIVITIES.map((activity) => {
          const isSelected = selectedActivities.includes(activity);
          return (
            <label
              key={activity}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                isSelected
                  ? 'border-navy-600 bg-navy-50'
                  : 'border-ink-200 bg-white hover:bg-ink-50 hover:border-ink-300'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleActivityToggle(activity)}
                disabled={disabled}
                className="sr-only"
              />
              <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                isSelected
                  ? 'border-navy-600 bg-navy-600'
                  : 'border-ink-300 bg-white'
              }`}>
                {isSelected && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className={`text-sm ${isSelected ? 'text-navy-900 font-medium' : 'text-ink-700'}`}>
                {activity}
              </span>
            </label>
          );
        })}
      </div>

      {/* Summary */}
      {selectionCount > 0 && (
        <div className="mt-4 pt-4 border-t border-ink-200">
          <p className="text-sm text-ink-500">
            <span className="font-medium">Selected activities:</span>{' '}
            {selectedActivities.join(', ')}
          </p>
        </div>
      )}
    </div>
  );
};

export default TILActivitiesSection;
